import {shareOrCopy} from "./share";

const favoriteHideDelay = 720;
const heartAnimationDelay = 820;
const favoriteFlyDelay = 820;

export default class ProgramFavorites {
    constructor($el) {
        this.$el = $el;
        this.favoriteIds = this.getInitialFavoriteIds();
        this.favoriteHideTimers = {};
        this.heartAnimationTimers = {};
        this.currentTimeTimeout = null;
        this.statusTimeout = null;

        this.bindTabs();
        this.bindHearts();
        this.bindShare();
        this.bindDownload();
        this.bindPrint();
        this.bindCurrentTime();
        this.updateUi();
    }

    bindTabs() {
        this.$el.querySelectorAll('[data-program-tab]').forEach(($tab) => {
            $tab.addEventListener('click', () => this.setActiveTab($tab.dataset.programTab));
        });
    }

    bindHearts() {
        this.$el.querySelectorAll('[data-program-heart]').forEach(($heart) => {
            $heart.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.toggleFavorite(parseInt($heart.dataset.programHeart, 10));
            });
        });
    }

    bindShare() {
        const $share = this.$el.querySelector('[data-program-share]');

        if (! $share) {
            return;
        }

        $share.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await this.share();
        });
    }

    bindPrint() {
        const $print = this.$el.querySelector('[data-program-print]');

        if (! $print) {
            return;
        }

        $print.addEventListener('click', () => {
            const printWindow = window.open(this.getPdfUrl(), '_blank');

            if (printWindow) {
                printWindow.addEventListener('load', () => printWindow.print(), {once: true});
            }
        });
    }

    bindDownload() {
        this.$el.querySelectorAll('[data-program-download]').forEach(($download) => {
            $download.addEventListener('click', () => {
                $download.href = this.getPdfUrl(true);
            });
        });
    }

    bindCurrentTime() {
        this.updateCurrentTime();
        this.scheduleCurrentTimeUpdate();

        document.addEventListener('visibilitychange', () => {
            if (! document.hidden) {
                this.updateCurrentTime();
                this.scheduleCurrentTimeUpdate();
            }
        });

        window.addEventListener('focus', () => {
            this.updateCurrentTime();
            this.scheduleCurrentTimeUpdate();
        });
    }

    scheduleCurrentTimeUpdate() {
        window.clearTimeout(this.currentTimeTimeout);

        const now = new Date();
        const nextMinuteDelay = 60000 - ((now.getSeconds() * 1000) + now.getMilliseconds()) + 100;

        this.currentTimeTimeout = window.setTimeout(() => {
            this.updateCurrentTime();
            this.scheduleCurrentTimeUpdate();
        }, nextMinuteDelay);
    }

    setActiveTab(tab) {
        this.$el.querySelectorAll('[data-program-tab]').forEach(($tab) => {
            const isActive = $tab.dataset.programTab === tab;
            $tab.classList.toggle('is-active', isActive);
            $tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.$el.querySelectorAll('[data-program-panel]').forEach(($panel) => {
            const isActive = $panel.dataset.programPanel === tab;
            $panel.classList.toggle('is-active', isActive);
            $panel.hidden = ! isActive;
        });

        this.updateDownloadUrl();
    }

    updateCurrentTime() {
        const $timeline = this.$el.querySelector('[data-program-timeline]');
        const $now = this.$el.querySelector('[data-program-now]');

        if (! $timeline || ! $now) {
            return;
        }

        const startMinutes = parseInt($timeline.dataset.startMinutes, 10);
        const endMinutes = parseInt($timeline.dataset.endMinutes, 10);
        const minuteHeight = parseFloat($timeline.dataset.minuteHeight);

        if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || Number.isNaN(minuteHeight)) {
            $now.hidden = true;
            return;
        }

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes() + (now.getSeconds() / 60);

        if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
            $now.hidden = true;
            return;
        }

        $now.style.setProperty('--program-now-offset', `${(currentMinutes - startMinutes) * minuteHeight}px`);
        $now.querySelector('[data-program-now-label]').textContent = `Nu ${this.formatTime(now)}`;
        $now.hidden = false;
    }

    formatTime(date) {
        return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
    }

    pad(number) {
        return String(number).padStart(2, '0');
    }

    toggleFavorite(id) {
        if (! id) {
            return;
        }

        const isRemoving = this.favoriteIds.includes(id);

        if (isRemoving) {
            this.favoriteIds = this.favoriteIds.filter((favoriteId) => favoriteId !== id);
        } else {
            this.favoriteIds.push(id);
        }

        this.favoriteIds.sort((a, b) => a - b);
        this.updateUi(id, isRemoving ? 'remove' : 'add');
        this.animateFavoriteChange(id, isRemoving ? 'remove' : 'add');
        this.sync();
    }

    updateUi(changedId = null, action = null) {
        this.$el.querySelectorAll('[data-program-session]').forEach(($card) => {
            const id = parseInt($card.dataset.programSession, 10);
            const isFavorite = this.favoriteIds.includes(id);

            $card.classList.toggle('is-favorite', isFavorite);

            if ($card.hasAttribute('data-program-favorite-card')) {
                this.updateFavoriteCardVisibility($card, id, isFavorite, changedId, action);
            }
        });

        this.$el.querySelectorAll('[data-program-heart]').forEach(($heart) => {
            const id = parseInt($heart.dataset.programHeart, 10);
            const isFavorite = this.favoriteIds.includes(id);
            const isRemoveHeart = $heart.hasAttribute('data-program-remove-heart');
            const ariaLabel = isRemoveHeart || isFavorite ? this.getRemoveLabel() : this.getAddLabel();
            const visibleLabel = isRemoveHeart
                ? this.getRemoveLabel()
                : (isFavorite ? this.getAddedLabel() : this.getAddLabel());

            $heart.setAttribute('aria-pressed', isFavorite ? 'true' : 'false');
            $heart.setAttribute('aria-label', ariaLabel);

            const $label = $heart.querySelector('[data-program-heart-label]');

            if ($label) {
                $label.textContent = visibleLabel;
            }
        });

        this.updateFavoriteCount();
        this.updateEmptyState();
        this.updateDownloadUrl();
    }

    updateFavoriteCardVisibility($card, id, isFavorite, changedId, action) {
        const timerKey = String(id);

        if (isFavorite) {
            window.clearTimeout(this.favoriteHideTimers[timerKey]);
            delete this.favoriteHideTimers[timerKey];
            $card.hidden = false;
            $card.classList.remove('is-removing');
            return;
        }

        if (id === changedId && action === 'remove' && ! $card.hidden) {
            const hideDelay = this.getFavoriteHideDelay();

            if (hideDelay === 0) {
                $card.hidden = true;
                return;
            }

            $card.classList.add('is-removing');
            window.clearTimeout(this.favoriteHideTimers[timerKey]);
            this.favoriteHideTimers[timerKey] = window.setTimeout(() => {
                if (! this.favoriteIds.includes(id)) {
                    $card.hidden = true;
                }

                $card.classList.remove('is-removing');
                delete this.favoriteHideTimers[timerKey];
                this.updateEmptyState();
            }, hideDelay);
            return;
        }

        if (! $card.classList.contains('is-removing')) {
            $card.hidden = true;
        }
    }

    updateEmptyState() {
        const $empty = this.$el.querySelector('[data-program-empty]');

        if (! $empty) {
            return;
        }

        $empty.hidden = this.favoriteIds.length > 0
            || this.$el.querySelector('[data-program-favorite-card].is-removing') !== null;
    }

    animateFavoriteChange(id, action) {
        const className = action === 'remove' ? 'is-removing' : 'is-adding';
        const previousClassName = action === 'remove' ? 'is-adding' : 'is-removing';

        if (action === 'add') {
            this.animateFavoriteFly(id);
        }

        this.$el.querySelectorAll(`[data-program-heart="${id}"]`).forEach(($heart, index) => {
            const timerKey = `${id}-${index}`;

            $heart.classList.remove(previousClassName, className);
            $heart.getBoundingClientRect();
            $heart.classList.add(className);

            window.clearTimeout(this.heartAnimationTimers[timerKey]);
            this.heartAnimationTimers[timerKey] = window.setTimeout(() => {
                $heart.classList.remove(className);
                delete this.heartAnimationTimers[timerKey];
            }, heartAnimationDelay);
        });
    }

    animateFavoriteFly(id) {
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const $source = this.$el.querySelector(`[data-program-panel="all"] [data-program-session="${id}"]`);
        const $target = this.$el.querySelector('[data-program-favorite-count]')
            || this.$el.querySelector('[data-program-tab="favorites"]');

        if (! $source || ! $target) {
            return;
        }

        const sourceRect = $source.getBoundingClientRect();
        const targetRect = $target.getBoundingClientRect();
        const $flyCard = $source.cloneNode(true);

        $flyCard.className = 'program-timetable__card program-timetable__fly-card';
        $flyCard.setAttribute('aria-hidden', 'true');
        $flyCard.removeAttribute('data-program-session');
        $flyCard.style.left = `${sourceRect.left}px`;
        $flyCard.style.top = `${sourceRect.top}px`;
        $flyCard.style.width = `${sourceRect.width}px`;
        $flyCard.style.height = `${sourceRect.height}px`;
        $flyCard.style.setProperty('--program-fly-x', `${targetRect.left + (targetRect.width / 2) - sourceRect.left - (sourceRect.width / 2)}px`);
        $flyCard.style.setProperty('--program-fly-y', `${targetRect.top + (targetRect.height / 2) - sourceRect.top - (sourceRect.height / 2)}px`);

        document.body.appendChild($flyCard);
        window.requestAnimationFrame(() => $flyCard.classList.add('is-flying'));
        window.setTimeout(() => $flyCard.remove(), favoriteFlyDelay);
    }

    getFavoriteHideDelay() {
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return 0;
        }

        return favoriteHideDelay;
    }

    async sync() {
        try {
            const response = await fetch(this.$el.dataset.favoritesUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify({speakerIds: this.favoriteIds}),
            });
            const data = await response.json();

            if (data.success) {
                this.favoriteIds = data.favoriteIds || this.favoriteIds;
                this.$el.dataset.shareUrl = data.shareUrl || '/programma';
                this.updateUi();
            }

            return data;
        } catch {
            this.showStatus('My finfest kon niet opgeslagen worden');
        }

        return null;
    }

    async share() {
        if (this.favoriteIds.length > 0) {
            await this.sync();
        }

        if (this.favoriteIds.length > 0 && ! this.hasFavoriteShareUrl()) {
            this.showStatus('Kon My finfest-link niet maken');
            return;
        }

        const shareData = this.getShareData();

        const result = await shareOrCopy(shareData);

        if (result === 'copied') {
            this.showStatus('Link gekopieerd');
        }
    }

    getShareData() {
        return {
            title: this.$el.dataset.shareTitle || document.title,
            text: this.favoriteIds.length > 0
                ? (this.$el.dataset.shareFavoritesText || this.$el.dataset.shareText || document.title)
                : (this.$el.dataset.shareText || document.title),
            url: this.getShareUrl(),
        };
    }

    getShareUrl() {
        return new URL(this.$el.dataset.shareUrl || '/programma', window.location.origin).toString();
    }

    hasFavoriteShareUrl() {
        const url = new URL(this.getShareUrl());

        return url.searchParams.has('favs');
    }

    getPdfUrl(download = false) {
        const url = new URL(this.$el.dataset.pdfUrl || '/ajax/program/pdf', window.location.origin);

        if (download) {
            url.searchParams.set('download', '1');
        }

        if (this.getActiveTab() === 'favorites') {
            url.searchParams.set('favorites', '1');
            url.searchParams.set('speakerIds', this.favoriteIds.join(','));
        }

        return url.toString();
    }

    getActiveTab() {
        const $activeTab = this.$el.querySelector('[data-program-tab].is-active');

        return $activeTab ? $activeTab.dataset.programTab : 'all';
    }

    updateDownloadUrl() {
        this.$el.querySelectorAll('[data-program-download]').forEach(($download) => {
            $download.href = this.getPdfUrl(true);
        });
    }

    updateFavoriteCount() {
        this.$el.querySelectorAll('[data-program-favorite-count]').forEach(($count) => {
            $count.textContent = String(this.favoriteIds.length);
        });
    }

    getAddLabel() {
        return this.$el.dataset.favoriteAddLabel || 'Toevoegen aan My finfest';
    }

    getAddedLabel() {
        return this.$el.dataset.favoriteAddedLabel || 'Toegevoegd';
    }

    getRemoveLabel() {
        return this.$el.dataset.favoriteRemoveLabel || 'Verwijderen uit My finfest';
    }

    showStatus(message) {
        let $status = this.$el.querySelector('[data-program-share-status]');

        if (! $status) {
            const $statusContainer = this.$el.querySelector('.program-timetable__top');

            if (! $statusContainer) {
                return;
            }

            $status = document.createElement('p');
            $status.className = 'program-timetable__share-status';
            $status.setAttribute('role', 'status');
            $status.setAttribute('aria-live', 'polite');
            $status.setAttribute('data-program-share-status', '');
            $statusContainer.appendChild($status);
        }

        $status.textContent = message;
        clearTimeout(this.statusTimeout);
        this.statusTimeout = setTimeout(() => {
            $status.textContent = '';
        }, 2400);
    }

    getInitialFavoriteIds() {
        const ids = [];

        this.$el.querySelectorAll('[data-program-heart][aria-pressed="true"]').forEach(($heart) => {
            const id = parseInt($heart.dataset.programHeart, 10);

            if (id && ! ids.includes(id)) {
                ids.push(id);
            }
        });

        return ids.sort((a, b) => a - b);
    }
}
